import { useState, type KeyboardEvent, type ReactNode } from 'react';

export interface Tab {
    id: string;
    label: ReactNode;
    content: ReactNode;
    disabled?: boolean;
    hidden?: boolean;
}

interface TabsProps {
    tabs: Tab[];
    activeTab?: string;
    defaultActiveTab?: string;
    onTabChange?: (tabId: string) => void;
    className?: string;
    tabsClassName?: string;
    contentClassName?: string;
    fill?: boolean;
    pills?: boolean;
    vertical?: boolean;
    smallTabs?: boolean;
}

const Tabs = ({
    tabs,
    activeTab: controlledActiveTab,
    defaultActiveTab,
    onTabChange,
    className = '',
    tabsClassName = '',
    contentClassName = '',
    fill = false,
    pills = false,
    vertical = false,
    smallTabs = false
}: TabsProps) => {
    const visibleTabs = tabs.filter((tab) => !tab.hidden);
    const firstVisibleTabId = visibleTabs.length > 0 ? visibleTabs[0].id : '';
    const [internalActiveTab, setInternalActiveTab] = useState(defaultActiveTab || firstVisibleTabId);
    const activeTab = controlledActiveTab ?? internalActiveTab;

    // Handle tab click
    const handleTabClick = (tabId: string) => {
        if (controlledActiveTab === undefined) {
            setInternalActiveTab(tabId);
        }

        if (onTabChange) {
            onTabChange(tabId);
        }
    };

    const handleTabKeyDown = (event: KeyboardEvent<HTMLButtonElement>, tab: Tab) => {
        if (tab.disabled) return;

        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleTabClick(tab.id);
        }
    };

    if (visibleTabs.length === 0) {
        return null;
    }

    // Container classes
    const containerClasses = [className, vertical ? 'flex' : ''].filter(Boolean).join(' ');

    // Nav classes
    const navClasses = [
        pills
            ? 'flex flex-wrap space-x-2'
            : vertical
              ? 'flex flex-col border-r border-zinc-700'
              : 'flex flex-wrap border-b border-zinc-700',
        fill ? 'w-full' : '',
        vertical ? 'mr-4' : '',
        smallTabs ? 'text-sm' : '',
        tabsClassName
    ]
        .filter(Boolean)
        .join(' ');

    // Vertical styles
    const verticalStyles = vertical ? { minWidth: '150px', height: 'fit-content' } : {};

    // Content classes
    const contentClasses = [vertical ? 'flex-grow' : 'mt-4', contentClassName].filter(Boolean).join(' ');

    return (
        <div className={containerClasses}>
            <ul className={navClasses} role="tablist" style={verticalStyles}>
                {visibleTabs.map((tab) => (
                    <li className={`${vertical ? 'mb-1' : 'mr-2'}`} key={tab.id} role="presentation">
                        <button
                            aria-controls={tab.id}
                            aria-selected={activeTab === tab.id}
                            className={`
                ${
                    pills
                        ? `px-4 py-2 rounded-md ${
                              activeTab === tab.id ? 'bg-p314-blue text-white' : 'bg-dark text-zinc-300 hover:bg-zinc-700'
                          }`
                        : `${vertical ? 'block px-4 py-2 border-l-4' : 'inline-block px-4 py-2 border-b-2 -mb-[2px]'}
                     ${
                         activeTab === tab.id
                             ? vertical
                                 ? 'border-l-p314-blue text-p314-blue'
                                 : 'border-b-p314-blue text-p314-blue'
                             : vertical
                               ? 'border-l-transparent hover:border-l-zinc-500 text-zinc-300 hover:text-white'
                               : 'border-b-transparent hover:border-b-zinc-500 text-zinc-300 hover:text-white'
                     }`
                }
                ${tab.disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                transition-colors duration-200
              `}
                            disabled={tab.disabled}
                            id={`${tab.id}-tab`}
                            role="tab"
                            type="button"
                            onClick={() => !tab.disabled && handleTabClick(tab.id)}
                            onKeyDown={(event) => handleTabKeyDown(event, tab)}
                        >
                            {tab.label}
                        </button>
                    </li>
                ))}
            </ul>

            <div className={contentClasses}>
                {visibleTabs.map((tab) => (
                    <div
                        aria-labelledby={`${tab.id}-tab`}
                        className={`${activeTab === tab.id ? 'block' : 'hidden'}`}
                        id={tab.id}
                        key={tab.id}
                        role="tabpanel"
                    >
                        {tab.content}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Tabs;
